'use client';

import React from 'react';
import { useRoomContext, useLocalParticipant } from '@livekit/components-react';
import toast from 'react-hot-toast';
import type { RemoteParticipant } from 'livekit-client';
import { isTrustedModerationSender } from '@/lib/dataChannelModeration';

export function AudioActivationNotifier() {
  const room = useRoomContext();
  const localParticipant = useLocalParticipant();

  // Listener global para receber mensagens de solicitação de ativação de áudio
  React.useEffect(() => {
    if (!room) return;

    const handleDataReceived = (payload: Uint8Array, participant?: RemoteParticipant) => {
      try {
        const data = JSON.parse(new TextDecoder().decode(payload));

        const moderationTypes = new Set([
          'request-audio-activation',
          'mute-participant',
          'disable-video',
          'mute-all',
        ]);
        if (moderationTypes.has(data.type) && !isTrustedModerationSender(participant)) {
          console.warn(
            'AudioActivationNotifier: ignorando mensagem de moderação de remetente não autorizado',
            { type: data.type },
          );
          return;
        }

        if (data.type === 'request-audio-activation') {
          const currentUserIdentity = localParticipant.localParticipant?.identity;

          // Mostrar toast apenas se a mensagem for destinada ao usuário atual
          // e não for enviada pelo próprio usuário
          const shouldShowToast =
            data.to === currentUserIdentity && data.from !== currentUserIdentity;

          if (shouldShowToast) {
            // Reproduzir sinal sonoro
            try {
              // Criar um som simples usando Web Audio API
              const audioContext = new (window.AudioContext ||
                (window as any).webkitAudioContext)();
              const oscillator = audioContext.createOscillator();
              const gainNode = audioContext.createGain();

              oscillator.connect(gainNode);
              gainNode.connect(audioContext.destination);

              oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // Frequência 800Hz
              oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1); // Mudança para 600Hz

              gainNode.gain.setValueAtTime(0, audioContext.currentTime);
              gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
              gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.2);

              oscillator.start(audioContext.currentTime);
              oscillator.stop(audioContext.currentTime + 0.2);

              console.log('🔊 Som reproduzido com sucesso!');
            } catch (error) {
              console.log('Erro ao reproduzir som:', error);

              // Fallback: tentar com Audio simples
              try {
                const audio = new Audio();
                audio.src =
                  'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT';
                audio.volume = 0.5;
                audio.play().catch((e) => console.log('Fallback audio também falhou:', e));
              } catch (fallbackError) {
                console.log('Fallback audio falhou:', fallbackError);
              }
            }

            toast(
              (t) => (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span className="flex-1">{data.message}</span>
                    <button
                      onClick={() => toast.dismiss(t.id)}
                      className="text-gray-500 hover:text-gray-700 text-sm ml-2"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="flex justify-center">
                    <button
                      onClick={() => {
                        // Ativar o microfone
                        if (room?.localParticipant?.setMicrophoneEnabled) {
                          room.localParticipant.setMicrophoneEnabled(true);
                        }
                        toast.dismiss(t.id);
                      }}
                      className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-bold hover:bg-blue-700 transition-colors"
                    >
                      Ativar som
                    </button>
                  </div>
                </div>
              ),
              {
                duration: 5000, // 5 segundos
                icon: '🔊',
                style: {
                  background: '#ffffff',
                  color: '#1f2937',
                  fontWeight: 'bold',
                  padding: '12px 16px',
                  border: '1px solid #e5e7eb',
                  boxShadow:
                    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                },
              },
            );
          }
        } else if (data.type === 'mute-participant') {
          const currentUserIdentity = localParticipant.localParticipant?.identity;

          // Mostrar toast apenas se a mensagem for destinada ao usuário atual
          // e não for enviada pelo próprio usuário
          const shouldShowToast =
            data.to === currentUserIdentity && data.from !== currentUserIdentity;

          if (shouldShowToast) {
            // Reproduzir sinal sonoro para silenciamento
            try {
              const audioContext = new (window.AudioContext ||
                (window as any).webkitAudioContext)();
              const oscillator = audioContext.createOscillator();
              const gainNode = audioContext.createGain();

              oscillator.connect(gainNode);
              gainNode.connect(audioContext.destination);

              // Som diferente para silenciamento (mais grave)
              oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
              oscillator.frequency.setValueAtTime(300, audioContext.currentTime + 0.1);

              gainNode.gain.setValueAtTime(0, audioContext.currentTime);
              gainNode.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + 0.01);
              gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3);

              oscillator.start(audioContext.currentTime);
              oscillator.stop(audioContext.currentTime + 0.3);

              console.log('🔇 Som de silenciamento reproduzido!');
            } catch (error) {
              console.log('Erro ao reproduzir som de silenciamento:', error);
            }

            // Silenciar o microfone do usuário atual
            if (room?.localParticipant?.setMicrophoneEnabled) {
              room.localParticipant.setMicrophoneEnabled(false);
            }

            toast(
              (t) => (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span className="flex-1">{data.message}</span>
                    <button
                      onClick={() => toast.dismiss(t.id)}
                      className="text-gray-500 hover:text-gray-700 text-sm ml-2"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ),
              {
                duration: 5000, // 5 segundos
                icon: '🔇',
                style: {
                  background: '#fef2f2',
                  color: '#dc2626',
                  fontWeight: 'bold',
                  padding: '12px 16px',
                  border: '1px solid #fecaca',
                  boxShadow:
                    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                },
              },
            );
          }
        } else if (data.type === 'disable-video') {
          const currentUserIdentity = localParticipant.localParticipant?.identity;

          // Mostrar toast apenas se a mensagem for destinada ao usuário atual
          // e não for enviada pelo próprio usuário
          const shouldShowToast =
            data.to === currentUserIdentity && data.from !== currentUserIdentity;

          if (shouldShowToast) {
            // Reproduzir sinal sonoro para desativação de vídeo
            try {
              const audioContext = new (window.AudioContext ||
                (window as any).webkitAudioContext)();
              const oscillator = audioContext.createOscillator();
              const gainNode = audioContext.createGain();

              oscillator.connect(gainNode);
              gainNode.connect(audioContext.destination);

              // Som diferente para desativação de vídeo (tom médio)
              oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
              oscillator.frequency.setValueAtTime(500, audioContext.currentTime + 0.1);

              gainNode.gain.setValueAtTime(0, audioContext.currentTime);
              gainNode.gain.linearRampToValueAtTime(0.25, audioContext.currentTime + 0.01);
              gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.25);

              oscillator.start(audioContext.currentTime);
              oscillator.stop(audioContext.currentTime + 0.25);

              console.log('📹 Som de desativação de vídeo reproduzido!');
            } catch (error) {
              console.log('Erro ao reproduzir som de desativação de vídeo:', error);
            }

            // Desativar a câmera do usuário atual
            if (room?.localParticipant?.setCameraEnabled) {
              room.localParticipant.setCameraEnabled(false);
            }

            toast(
              (t) => (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span className="flex-1">{data.message}</span>
                    <button
                      onClick={() => toast.dismiss(t.id)}
                      className="text-gray-500 hover:text-gray-700 text-sm ml-2"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ),
              {
                duration: 5000, // 5 segundos
                icon: '📹',
                style: {
                  background: '#fef3c7',
                  color: '#d97706',
                  fontWeight: 'bold',
                  padding: '12px 16px',
                  border: '1px solid #fed7aa',
                  boxShadow:
                    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                },
              },
            );
          }
        } else if (data.type === 'mute-all') {
          const currentUserIdentity = localParticipant.localParticipant?.identity;

          // Mostrar toast para todos os participantes (exceto o anfitrião)
          const shouldShowToast = data.from !== currentUserIdentity;

          if (shouldShowToast) {
            // Reproduzir sinal sonoro para silenciamento em massa
            try {
              const audioContext = new (window.AudioContext ||
                (window as any).webkitAudioContext)();
              const oscillator = audioContext.createOscillator();
              const gainNode = audioContext.createGain();

              oscillator.connect(gainNode);
              gainNode.connect(audioContext.destination);

              // Som para silenciamento em massa (sequência de tons)
              oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
              oscillator.frequency.setValueAtTime(300, audioContext.currentTime + 0.1);
              oscillator.frequency.setValueAtTime(200, audioContext.currentTime + 0.2);

              gainNode.gain.setValueAtTime(0, audioContext.currentTime);
              gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
              gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.4);

              oscillator.start(audioContext.currentTime);
              oscillator.stop(audioContext.currentTime + 0.4);

              console.log('🔇 Som de silenciamento em massa reproduzido!');
            } catch (error) {
              console.log('Erro ao reproduzir som de silenciamento em massa:', error);
            }

            // Silenciar o microfone do usuário atual
            if (room?.localParticipant?.setMicrophoneEnabled) {
              room.localParticipant.setMicrophoneEnabled(false);
            }

            toast(
              (t) => (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span className="flex-1">{data.message}</span>
                    <button
                      onClick={() => toast.dismiss(t.id)}
                      className="text-gray-500 hover:text-gray-700 text-sm ml-2"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ),
              {
                duration: 5000, // 5 segundos
                icon: '🔇',
                style: {
                  background: '#fef2f2',
                  color: '#dc2626',
                  fontWeight: 'bold',
                  padding: '12px 16px',
                  border: '1px solid #fecaca',
                  boxShadow:
                    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                },
              },
            );
          }
        }
      } catch (e) {
        console.error('Erro ao processar mensagem:', e);
      }
    };

    room.on('dataReceived', handleDataReceived);

    return () => {
      room.off('dataReceived', handleDataReceived);
    };
  }, [room, localParticipant.localParticipant?.identity]);

  // Este componente não renderiza nada visualmente
  return null;
}
